import React, { useState, useEffect, useContext, useReducer } from 'react';
import {
    Container,
    Card,
    CardBody,
    Row,
    Col,
    FormGroup,
    Label,
    Button,
    Input,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'reactstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider, { CSVExport } from 'react-bootstrap-table2-toolkit';
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone
} from 'react-bootstrap-table2-paginator';
import classNames from 'classnames';
import styles from './PriceTool.module.css';
import { SessionContext } from '../../context/SessionContext';
import { DropDownAutoComplete } from '../../components/inputs/DropDownAutoComplete';
import MultiDropDown from '../shared/MultiDropDown/MultiDropDown';
import AlertBanner from '../shared/AlertBanner/AlertBanner';
import SnackBarAlert from '../shared/SnackBar/SnackBarAlert';
import { ReactComponent as CopyIcon } from '../../assets/images/Vendor/Staples/copy.svg';
import { ReactComponent as ExportIcon } from '../../assets/images/box-arrow-out.svg';
import SpinnerButton from '../shared/SpinnerButton/SpinnerButton';
import InfoTooltip from '../shared/InfoTooltip/InfoTooltip';

const PriceTool = () => {
    const context = useContext(SessionContext);
    const { accountName, getCurrentTabAndSubtab } = context;
    const { tab } = getCurrentTabAndSubtab();
    const [programs, setPrograms] = useState([]);
    const [programCategories, setProgramCategories] = useState([]);
    const [attributes, setAttributes] = useState([]);
    const [retailPrices, setRetailPrices] = useState([]);
    const [retailPricesData, setRetailPricesData] = useState([]);
    const [allDistinctRetailFilters, setAllDistinctRetailFilters] = useState([]);
    const [terms, setTerms] = useState([]);
    const [termsData, setTermsData] = useState([]);
    const [repairTypes, setRepairTypes] = useState([]);
    const [repairTypesData, setRepairTypesData] = useState([]);
    const [coverages, setCoverages] = useState([]);
    const [program, setProgram] = useState();
    const [programCategory, setProgramCategory] = useState();
    const [attribute, setAttribute] = useState();
    const [retailPrice, setRetailPrice] = useState([]);
    const [term, setTerm] = useState([]);
    const [repairType, setRepairType] = useState([]);
    const [coverage, setCoverage] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [keyField, setKeyField] = useState('skuNumber');
    const [defaultSortedBy, setDefaultSortedBy] = useState([
        {
            dataField:
                tab?.priceToolRecords?.sortData?.sortField !== undefined
                    ? tab?.priceToolRecords?.searchResults.length > 0
                        ? tab?.priceToolRecords?.sortData?.sortField
                        : 'retailRange'
                    : 'retailRange',
            order:
                tab?.priceToolRecords?.sortData?.sortOrder !== undefined
                    ? tab?.priceToolRecords?.searchResults.length > 0
                        ? tab?.priceToolRecords?.sortData?.sortOrder
                        : 'asc'
                    : 'asc'
        }
    ]);
    const [selectedMulipleRow, setSelectedMultipleRow] = useState([]);
    const [selectedMultipleRowData, setSelectedMultipleRowData] = useState([]);
    const [selectedRow, setSelectedRow] = useState(false);
    const [csvData, setCsvData] = useState([]);
    const [copiedValue, setCopiedValue] = useState('');
    const [validationCheck, setValidationCheck] = useState(false);
    const [paging, setPaging] = useState(
        tab?.priceToolRecords?.paging !== undefined ? tab?.priceToolRecords?.paging : 15
    );
    const [filterOptions, setFilterOptions] = useState([]);
    const [clearStatus, setClearStatus] = useState(false);
    const [sortData, setSortData] = useState({
        sortField:
            tab?.priceToolRecords?.sortData?.sortField !== undefined
                ? tab?.priceToolRecords?.searchResults.length > 0
                    ? tab?.priceToolRecords?.sortData?.sortField
                    : 'retailRange'
                : 'retailRange',
        sortOrder:
            tab?.priceToolRecords?.sortData?.sortOrder !== undefined
                ? tab?.priceToolRecords?.searchResults.length > 0
                    ? tab?.priceToolRecords?.sortData?.sortOrder
                    : 'asc'
                : 'asc'
    });
    const [showLoader, setShowLoader] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [categoryDisabled, setCategoryDisabled] = useState(true);
    const [attributeDisabled, setAttributeDisabled] = useState(true);
    const [listDisabled, setListDisabled] = useState(true);
    const [showSpinner, setShowSpinner] = useState(false);
    const [noResultsFound, setNoResultsFound] = useState(false);
    const [disableCsvExport, setDisableCsvExport] = useState(true);

    const sizePerPageOptionRenderer = ({ text, page, onSizePerPageChange }) => (
        <li
            key={text}
            role='presentation'
            className={classNames('dropdown-item', styles.pageItem)}
            data-page={page}
            onMouseDown={(e) => {
                e.preventDefault();
                setPaging(page);
                onSizePerPageChange(page);
            }}
        >
            {text}
        </li>
    );

    const options = {
        custom: true,
        activePageNumber: 1,
        sizePerPage: paging,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        pageStartIndex: 1,
        totalSize: searchResults.length,
        sizePerPageList: [
            { text: '15', value: 15 },
            { text: '25', value: 25 },
            { text: '50', value: 50 },
            { text: '100', value: 100 }
        ],
        sizePerPageOptionRenderer
    };

    const manageSort = (field, order) => {
        setSortData({
            sortField: field,
            sortOrder: order
        });
        setDefaultSortedBy([
            {
                dataField: field,
                order: order
            }
        ]);
    };

    const searchColumns = [
        {
            dataField: 'retailRange',
            text: 'Product Retail Price',
            sort: true,
            headerFormatter: headerFormatter,
            headerStyle: widthFormatter('rangeCol'),
            style: widthFormatter('rangeCol'),
            onSort: (field, order) => {
                manageSort(field, order);
            }
        },
        {
            dataField: 'term',
            text: 'Term',
            sort: true,
            headerFormatter: headerFormatter,
            headerStyle: widthFormatter('termCol'),
            style: widthFormatter('termCol'),
            onSort: (field, order) => {
                manageSort(field, order);
            }
        },
        {
            dataField: 'repairType',
            text: 'Repair Type',
            sort: true,
            headerFormatter: headerFormatter,
            headerStyle: widthFormatter('repairCol'),
            style: widthFormatter('repairCol'),
            onSort: (field, order) => {
                manageSort(field, order);
            }
        },
        {
            dataField: 'coverage',
            text: 'ADH Coverage',
            sort: true,
            headerFormatter: headerFormatter,
            headerStyle: widthFormatter('coverageCol'),
            style: widthFormatter('coverageCol'),
            onSort: (field, order) => {
                manageSort(field, order);
            }
        },
        {
            dataField: 'description',
            text: 'Description',
            sort: true,
            headerFormatter: headerFormatter,
            headerStyle: widthFormatter('descriptionCol'),
            style: widthFormatter('descriptionCol'),
            onSort: (field, order) => {
                manageSort(field, order);
            }
        },
        {
            dataField: 'retail',
            text: 'Plan Retail',
            sort: true,
            headerFormatter: headerFormatter,
            headerStyle: widthFormatter('retailCol'),
            formatter: (col, row) => {
                return <span>${parseFloat(col).toFixed(2)}</span>;
            },
            style: (cell, row, rowIndex, colIndex) => {
                return { width: widthFormatter('retailCol'), textAlign: 'right', marginRight: '10px' };
            },
            onSort: (field, order) => {
                manageSort(field, order);
            }
        },
        {
            dataField: 'skuNumber',
            text: 'SKU Number',
            sort: true,
            headerFormatter: headerFormatter,
            headerStyle: widthFormatter('skuCol'),
            style: widthFormatter('skuCol'),
            onSort: (field, order) => {
                manageSort(field, order);
            }
        },
        {
            dataField: 'skuNumber',
            text: '',
            sort: false,
            formatter: (col, row) => {
                return (
                    <span className={styles.copyIcon}>
                        <CopyIcon
                            className={styles.copy}
                            onClick={() => {
                                handleSKUCopy(row);
                            }}
                        />
                    </span>
                );
            },
            headerStyle: widthFormatter('copyCol'),
            style: widthFormatter('copyCol')
        }
    ];

    const selectRow = {
        mode: 'checkbox',
        clickToSelect: false,
        classes: '',
        onSelect: handleSelect,
        onSelectAll: (isSelect, rows) => {
            if (isSelect) {
                setSelectedMultipleRow(rows.map((row) => row.skuNumber));
                setSelectedMultipleRowData(rows);
            } else {
                setSelectedMultipleRow([]);
                setSelectedMultipleRowData([]);
            }
        },
        selected: selectedMulipleRow,
        style: { backgroundColor: '#efefef' },
        selectionRenderer: ({ mode, checked, disabled }) => (
            <div className={classNames('checkbox', 'mb-0')}>
                <Input
                    className='mr-2'
                    type='checkbox'
                    checked={checked}
                />
                <Label className={classNames('checkboxLabel', accountName)}></Label>
            </div>
        ),
        selectionHeaderRenderer: ({ mode, checked, disabled }) => (
            <div className={classNames('checkbox', 'mb-0')}>
                <Input
                    className='mr-2'
                    type='checkbox'
                    checked={checked}
                />
                <Label className={classNames('checkboxLabel', accountName)}></Label>
            </div>
        )
    };

    function widthFormatter(width) {
        const widths = {
            rangeCol: '20%',
            termCol: '6%',
            repairCol: '16%',
            coverageCol: '14%',
            descriptionCol: '17%',
            retailCol: '13%',
            skuCol: '12%',
            copyCol: '2%'
        };

        return {
            width: widths[width]
        };
    }

    function headerFormatter(column, index, { sortElement, filterElement }) {
        let { order } = sortElement.props;
        const textWithHtml = `<b>Depot:</b> Product shipped in for repair service<br />
                                <b>Onsite:</b> Servicer will repair product on location in \"less than 10 days\"<br />
                                <b>Next Day Onsite:</b> Servicer will repair or diagnose product on location in the next business day<br />
                                <b>Credit:</b> Customer will be reimbursed for product cost via account credit`;
        const msg = <span dangerouslySetInnerHTML={{ __html: textWithHtml }} />;
        return (
            <span className={`${order === 'asc' ? styles.ascending : ''} ${order === 'desc' ? styles.descending : ''}`}>
                {column.text}
                {column.dataField === 'repairType' && (
                    <>
                        <span className='ml-1'>
                            <InfoTooltip
                                id={index}
                                iconType='info'
                                message={msg}
                                cssClass={styles.retailType}
                                cssTextClass={styles.retailTypeInfoText}
                            />
                        </span>
                    </>
                )}
                {column.dataField === 'retail' && (
                    <>
                        <span className='ml-1'>
                            <InfoTooltip
                                id={index}
                                iconType='info'
                                message='Product Retail Price cannot be lower than cost.'
                                cssClass={styles.retailType}
                            />
                        </span>
                    </>
                )}
            </span>
        );
    }

    const handleSKUCopy = (row) => {
        setCopiedValue(row.skuNumber);
        navigator.clipboard
            .writeText(row.skuNumber)
            .then(() => {
                setShowAlert(true);
            })
            .catch((err) => {
                console.error('Failed to copy ===>>> ', err);
                setShowAlert(false);
            });
        setShowAlert(true);
    };

    function handleSelect(row, isSelect, rowIndex, e) {
        if (isSelect) {
            setSelectedMultipleRow([...selectedMulipleRow, row.skuNumber]);
            setSelectedMultipleRowData([...selectedMultipleRowData, row]);
            setCsvData([...selectedMultipleRowData, row]);
        } else {
            setSelectedMultipleRow([...selectedMulipleRow].filter((x) => x !== row.skuNumber));
            setSelectedMultipleRowData([...selectedMultipleRowData].filter((x) => x.skuNumber !== row.skuNumber));
            setCsvData([...selectedMultipleRowData].filter((x) => x.skuNumber !== row.skuNumber));
        }
    }

    const setupFilterOptions = async () => {
        try {
            setShowLoader(true);
            await fetch('account/GetAssetsByAccountId/45', {
                method: 'get',
                headers: {
                    'Content-Type': 'application/json'
                }
            }).then((res) => {
                if (res.status === 200) {
                    res.json().then((data) => {
                        setFilterOptions(data);
                        const results = data.map((rec, index) => ({
                            data: rec.programId,
                            display: rec.programName,
                            id: index
                        }));
                        setPrograms(results);
                        setShowLoader(false);
                    });
                }
            });
        } catch (error) {
            console.log('Error ===>>> ', error);
        }
    };

    const handleProgramNameChange = (val) => {
        setProgram(val);
        setClearStatus(false);
        setCategoryDisabled(false);
        setAttributeDisabled(true);

        filterOptions.map((rec, index) => {
            if (rec.programId === val) {
                const categories = rec.assets.map((item, i) => ({
                    data: item.assetName,
                    display: item.assetName,
                    id: i
                }));
                const categoriesSorted = categories.sort((a, b) => {
                    if (a.display < b.display) return -1;
                    if (a.display > b.display) return 1;
                    return 0;
                });
                setProgramCategories(categoriesSorted);
                setAttributes([]);
            }
        });
    };

    const handleProgramCategoryChange = (val) => {
        setProgramCategory(val);
        setSearchResults([]);

        setSortData({
            sortField: 'retailRange',
            sortOrder: 'asc'
        });
        setDefaultSortedBy([
            {
                dataField: 'retailRange',
                order: 'asc'
            }
        ]);

        const defaultSelection = [];
        setAttribute('');
        setPaging(15);
        filterOptions
            .filter((fo) => fo.programId === program)
            .map((rec, index) => {
                const assets = rec.assets.find((a) => a.assetName.toLowerCase() === val.toLowerCase());
                console.log({ context });
                if (assets != null && typeof assets != 'undefined') {
                    const atts = assets?.attributes
                        ?.filter((a) => a.attributeId !== 0 && typeof a !== 'undefined')
                        ?.map((item, i) => ({
                            data: item.attributeName,
                            display: item.attributeName,
                            id: i
                        }));
                    const attributesSorted = atts.sort((a, b) => {
                        if (a.display < b.display) return -1;
                        if (a.display > b.display) return 1;
                        return 0;
                    });

                    if (atts.length > 0) {
                        setAttributeDisabled(false);
                        setValidationCheck(false);
                        setAttributes(attributesSorted);
                    } else {
                        setAttributeDisabled(true);
                        setValidationCheck(true);
                        setListDisabled(false);
                        setAttributes([]);
                    }

                    let retails = assets?.retailRanges.map((item, i) => ({
                        display: item,
                        value: item
                    }));
                    retails.unshift({ display: 'Select All', value: 'all' });
                    let retailsData = assets?.retailRanges.map((item, i) => item);
                    retailsData.unshift('all');

                    let repairTypes = assets?.repairTypes.map((item, i) => ({
                        display: item.repairTypeName,
                        value: item.repairTypeName
                    }));
                    repairTypes.unshift({ display: 'Select All', value: 'all' });
                    let repairTypesData = assets?.repairTypes.map((item, i) => item.repairTypeName);
                    repairTypesData.unshift('all');

                    const coverages = assets?.coverages.map((item, i) => ({
                        data: item.coverageName,
                        display: item.coverageName,
                        id: i
                    }));

                    let terms = assets?.terms.map((item, i) => ({
                        data: item.termName,
                        display: item.termName,
                        value: item.termName,
                        id: i
                    }));
                    terms.unshift({ display: 'Select All', value: 'all' });
                    let termsData = assets?.terms.map((item, i) => item.termName);
                    termsData.unshift('all');

                    setRetailPrices(retails);
                    setRetailPricesData(retailsData);
                    setAllDistinctRetailFilters(retailsData);
                    setTerms(terms);
                    setTermsData(termsData);
                    setRepairTypes(repairTypes);
                    setRepairTypesData(repairTypesData);
                    setCoverages(coverages);

                    setRetailPrice(retailsData);
                    setTerm(termsData);
                    setRepairType(repairTypesData);
                    setCoverage('');
                }
            });
    };

    const handleAttributeChange = (val) => {
        setAttribute(val);
        setValidationCheck(true);
        setListDisabled(false);

        setSearchResults([]);

        setSortData({
            sortField: 'retailRange',
            sortOrder: 'asc'
        });

        setDefaultSortedBy([
            {
                dataField: 'retailRange',
                order: 'asc'
            }
        ]);

        setPaging(15);

        const defaultSelection = [];
        filterOptions
            .filter((fo) => fo.programId === program)
            .map((rec, index) => {
                const assets = rec.assets.find((a) => a.assetName.toLowerCase() === programCategory.toLowerCase());
                const att = assets.attributes.find((a) => a.attributeName.toLowerCase() === val.toLowerCase());

                if (assets != null && typeof assets != 'undefined' && att !== null && typeof att != undefined) {
                    let retails = assets?.retailRanges.map((item, i) => ({
                        display: item,
                        value: item
                    }));
                    retails.unshift({ display: 'Select All', value: 'all' });
                    let retailsData = assets?.retailRanges.map((item, i) => item);
                    retailsData.unshift('all');

                    let repairTypes = assets?.repairTypes.map((item, i) => ({
                        display: item.repairTypeName,
                        value: item.repairTypeName
                    }));
                    repairTypes.unshift({ display: 'Select All', value: 'all' });
                    let repairTypesData = assets?.repairTypes.map((item, i) => item.repairTypeName);
                    repairTypesData.unshift('all');

                    const coverages = assets?.coverages.map((item, i) => ({
                        data: item.coverageName,
                        display: item.coverageName,
                        id: i
                    }));

                    let terms = assets?.terms.map((item, i) => ({
                        data: item.termName,
                        display: item.termName,
                        value: item.termName,
                        id: i
                    }));
                    terms.unshift({ display: 'Select All', value: 'all' });
                    let termsData = assets?.terms.map((item, i) => item.termName);
                    termsData.unshift('all');

                    setRetailPrices(retails);
                    setRetailPricesData(retailsData);
                    setAllDistinctRetailFilters(retailsData);
                    setTerms(terms);
                    setTermsData(termsData);
                    setRepairTypes(repairTypes);
                    setRepairTypesData(repairTypesData);
                    setCoverages(coverages);

                    setRetailPrice(defaultSelection);
                    setTerm(defaultSelection);
                    setRepairType(defaultSelection);
                    setCoverage('');
                }
            });
    };

    const handleRetailPrice = (val) => {
        let tempArray = [...retailPrice];
        const index = tempArray.indexOf(val);
        if (index > -1) {
            if (val === 'all') {
                tempArray = [];
            } else {
                tempArray.splice(index, 1);
                if (tempArray.indexOf('all') > -1) {
                    tempArray.splice(tempArray.indexOf('all'), 1);
                }
            }
        } else {
            if (val === 'all') {
                tempArray = [...retailPricesData];
            } else {
                tempArray.push(val);
                if (tempArray.length === retailPrices.length - 1) {
                    tempArray.push('all');
                }
            }
        }
        setRetailPrice(tempArray);
    };

    const handleTerm = (val) => {
        let termArray = [...term];
        const index = termArray.indexOf(val);
        if (index > -1) {
            if (val === 'all') {
                termArray = [];
            } else {
                termArray.splice(index, 1);
                if (termArray.indexOf('all') > -1) {
                    termArray.splice(termArray.indexOf('all'), 1);
                }
            }
        } else {
            if (val === 'all') {
                termArray = [...termsData];
            } else {
                termArray.push(val);
                if (termArray.length === terms.length) {
                    termArray.push('all');
                }
            }
        }
        setTerm(termArray);
    };

    const handleRepairType = (val) => {
        let repairArray = [...repairType];
        const index = repairArray.indexOf(val);
        if (index > -1) {
            if (val === 'all') {
                repairArray = [];
            } else {
                repairArray.splice(index, 1);
                if (repairArray.indexOf('all') > -1) {
                    repairArray.splice(repairArray.indexOf('all'), 1);
                }
            }
        } else {
            if (val === 'all') {
                repairArray = [...repairTypesData];
            } else {
                repairArray.push(val);
                if (repairArray.length === repairTypes.length - 1) {
                    repairArray.push('all');
                }
            }
        }
        setRepairType(repairArray);
    };

    const handleCoverage = (val) => {
        setCoverage(val);
    };

    const handleClear = () => {
        const priceToolRecords = {};
        context.tabDispatch('UPDATE_TAB', { priceToolRecords });
        setProgram('');
        setProgramCategory('');
        setAttribute('');
        setRetailPrice([]);
        setTerm([]);
        setRepairType([]);
        setCoverage('');
        setValidationCheck(false);
        setSearchResults([]);
        setCategoryDisabled(true);
        setAttributeDisabled(true);
        setListDisabled(true);
        manageSort('retailRange', 'asc');
        setAttributes([]);
        setPaging(15);
    };

    const handleSearch = (e) => {
        const tempRetailPrice = retailPrice.includes('all') ? [] : retailPrice.filter((item) => item !== 'all');
        const tempTerm = retailPrice.includes('all') ? [] : term.filter((item) => item !== 'all');
        const tempRepairType = retailPrice.includes('all') ? [] : repairType.filter((item) => item !== 'all');

        try {
            setSearchResults([]);
            setShowLoader(true);
            setShowSpinner(true);
            fetch('pricing/SalesLookUp', {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    programId: program,
                    filterProductCategory: programCategory,
                    filterAttribute: attribute,
                    FilterRetailRange: tempRetailPrice.toString(),
                    FilterTermName: tempTerm.toString(),
                    FilterRepairType: tempRepairType.toString(),
                    FilterCoverage: coverage
                })
            }).then((res) => {
                if (res.status === 204) {
                    setShowLoader(false);
                    setShowSpinner(false);
                    setNoResultsFound(true);
                    return false;
                } else if (res.status === 200) {
                    res.json().then((data) => {
                        if (programCategory === 'Accessories') {
                            //Accessories category requires unique cases only for table functionality, no other category requires
                            data.map((row, index) => {
                                setSearchResults((prevResults) => {
                                    const isDuplicate = prevResults.some(
                                        (item) =>
                                            item.skuNumber === row.skuNumber
                                            && item.retail === row.retail
                                            && item.retailRange === row.retailRange
                                            && item.coverage === row.coverage
                                            && item.description === row.description
                                            && item.repairType === row.repairType
                                            && item.term === row.term
                                    );
                                    if (!isDuplicate) {
                                        return [
                                            ...prevResults,
                                            {
                                                id: index,
                                                skuNumber: row.skuNumber,
                                                retail: row.retail,
                                                retailRange: row.retailRange,
                                                coverage: row.coverage,
                                                description: row.description,
                                                repairType: row.repairType,
                                                term: row.term
                                            }
                                        ];
                                    } else {
                                        return prevResults;
                                    }
                                });
                            });
                        } else {
                            setSearchResults(data);
                        }
                        setShowLoader(false);
                        setShowSpinner(false);
                        if (data.length === 0) {
                            setNoResultsFound(true);
                        } else {
                            setNoResultsFound(false);
                        }
                        const filterOptions = {
                            program: program,
                            programCategory: programCategory,
                            attribute: attribute,
                            retailPrice: retailPrice,
                            term: term,
                            repairType: repairType,
                            coverage: coverage,
                            programCategories: programCategories,
                            attributes: attributes,
                            retails: retailPrices,
                            retailsData: retailPricesData,
                            terms: terms,
                            termsData: termsData,
                            repairTypes: repairTypes,
                            repairTypesData: repairTypesData,
                            coverages: coverages,
                            categoryDisabled: categoryDisabled,
                            attributeDisabled: attributeDisabled,
                            listDisabled: listDisabled,
                            validationCheck: validationCheck
                        };
                        const priceToolRecords = {
                            filterOptions: filterOptions,
                            searchResults: data,
                            sortData: sortData,
                            paging: paging
                        };
                        context.tabDispatch('UPDATE_TAB', { priceToolRecords });
                    });
                }
            });
        } catch (error) {
            console.log('error ===>>> ', error);
        }
    };

    const handleExport = async () => {
        let route = '';
        const fileDownload = 'export-priceList';
        let param = '';
        let exportAll = false;

        if (csvData.length !== 0) {
            param = csvData.map((d) => d.skuNumber);
            exportAll = false;
        }

        const attributeParam = attribute !== '' ? attribute : null;
        route = `pricing/ExportSelectedSalesLookupSkus/${program}/${programCategory}/${attributeParam}/${exportAll}`;

        const res = await fetch(route, {
            method: 'post',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(param)
        });

        const data = await res.blob();
        if (data != undefined) {
            const file = new Blob([data], { type: 'text/csv' });
            const fileURL = window.URL.createObjectURL(file);

            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.download = fileDownload;
            fileLink.click();
        } else {
            console.err('Error fetching file.');
            return undefined;
        }
    };

    const handleExportAll = async () => {
        let route = '';
        const fileDownload = 'export-priceList';
        let param = '';
        let exportAll = true;

        param = searchResults.map((d) => d.skuNumber);
        const attributeParam = attribute !== '' ? attribute : null;
        route = `pricing/ExportSelectedSalesLookupSkus/${program}/${programCategory}/${attributeParam}/${exportAll}`;

        const res = await fetch(route, {
            method: 'post',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(param)
        });

        const data = await res.blob();
        if (data != undefined) {
            const file = new Blob([data], { type: 'text/csv' });
            const fileURL = window.URL.createObjectURL(file);

            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.download = fileDownload;
            fileLink.click();
        } else {
            console.err('Error fetching file.');
            return undefined;
        }
    };

    const updateTabState = () => {
        const filterOptions = {
            program: program,
            programCategory: programCategory,
            attribute: attribute,
            retailPrice: retailPrice,
            term: term,
            repairType: repairType,
            coverage: coverage,
            programCategories: programCategories,
            attributes: attributes,
            retails: retailPrices,
            retailsData: retailPricesData,
            terms: terms,
            termsData: termsData,
            repairTypes: repairTypes,
            repairTypesData: repairTypesData,
            coverages: coverages,
            categoryDisabled: categoryDisabled,
            listDisabled: listDisabled,
            validationCheck: validationCheck,
            attributeDisabled: attributeDisabled
        };
        const priceToolRecords = {
            filterOptions: filterOptions,
            searchResults: searchResults,
            sortData: sortData,
            paging: paging
        };
        context.tabDispatch('UPDATE_TAB', { priceToolRecords });
    };

    useEffect(() => {
        setupFilterOptions();

        if (tab?.priceToolRecords?.searchResults?.length > 0) {
            setSearchResults(tab?.priceToolRecords?.searchResults);
            setDefaultSortedBy([
                {
                    dataField:
                        tab?.priceToolRecords?.sortData?.sortField !== undefined
                            ? tab?.priceToolRecords?.sortData?.sortField
                            : 'retailRange',
                    order:
                        tab?.priceToolRecords?.sortData?.sortOrder !== undefined
                            ? tab?.priceToolRecords?.sortData?.sortOrder
                            : 'asc'
                }
            ]);
            setSortData({
                sortField:
                    tab?.priceToolRecords?.sortData?.sortField !== undefined
                        ? tab?.priceToolRecords?.sortData?.sortField
                        : 'retailRange',
                sortOrder:
                    tab?.priceToolRecords?.sortData?.sortOrder !== undefined
                        ? tab?.priceToolRecords?.sortData?.sortOrder
                        : 'asc'
            });
            setPaging(tab?.priceToolRecords?.paging !== undefined ? tab?.priceToolRecords?.paging : 15);
        }
        if (tab?.priceToolRecords?.filterOptions?.program !== undefined) {
            setProgramCategories(tab?.priceToolRecords?.filterOptions?.programCategories);
            setRetailPrices(tab?.priceToolRecords?.filterOptions?.retails);
            setRetailPricesData(tab?.priceToolRecords?.filterOptions?.retailsData);
            setAllDistinctRetailFilters(tab?.priceToolRecords?.filterOptions?.retailsData);
            setTerms(tab?.priceToolRecords?.filterOptions?.terms);
            setTermsData(tab?.priceToolRecords?.filterOptions?.termsData);
            setRepairTypes(tab?.priceToolRecords?.filterOptions?.repairTypes);
            setRepairTypesData(tab?.priceToolRecords?.filterOptions?.repairTypesData);
            setCoverages(tab?.priceToolRecords?.filterOptions?.coverages);
            setCategoryDisabled(tab?.priceToolRecords?.filterOptions?.categoryDisabled);
            setAttributeDisabled(tab?.priceToolRecords?.filterOptions?.attributeDisabled);
            setListDisabled(tab?.priceToolRecords?.filterOptions?.listDisabled);
            setValidationCheck(tab?.priceToolRecords?.filterOptions?.validationCheck);
            setProgram(tab?.priceToolRecords?.filterOptions?.program);
            setProgramCategory(tab?.priceToolRecords?.filterOptions?.programCategory);
            setRetailPrice(tab?.priceToolRecords?.filterOptions?.retailPrice);
            setTerm(tab?.priceToolRecords?.filterOptions?.term);
            setRepairType(tab?.priceToolRecords?.filterOptions?.repairType);
            setCoverage(tab?.priceToolRecords?.filterOptions?.coverage);
            setAttribute(tab?.priceToolRecords?.filterOptions?.attribute);
            setAttributes(tab?.priceToolRecords?.filterOptions?.attributes);
        }

        const exportStatus = csvData.length == 0 ? true : false;
        setDisableCsvExport(exportStatus);
    }, []);

    useEffect(() => {
        const exportStatus = csvData.length == 0 ? true : false;
        setDisableCsvExport(exportStatus);
    }, [csvData]);

    useEffect(() => {
        updateTabState();
    }, [program, programCategory, attribute, retailPrice, term, repairType, coverage, searchResults, sortData, paging]);

    return (
        <>
            {showAlert && (
                <SnackBarAlert
                    content='SKU Number copied to clipboard'
                    onClose={() => setShowAlert(false)}
                    showSnackBar={showAlert}
                />
            )}
            <Container className='contentWrapper'>
                <h1 className='h3'>Plan Pricing</h1>
                <Card>
                    <CardBody>
                        <Row className='multiColWrapper'>
                            <Col
                                md='3'
                                className='col-5ths'
                            >
                                <FormGroup>
                                    <Label
                                        className='form-label'
                                        aria-label='Program Name'
                                        for='programName'
                                    >
                                        Program Name
                                    </Label>
                                    <span
                                        aria-hidden='true'
                                        className='required-indicator'
                                    >
                                        *
                                    </span>
                                    <DropDownAutoComplete
                                        name='programName'
                                        id='programName'
                                        options={programs}
                                        onChangedValue={handleProgramNameChange}
                                        value={program}
                                        defaultValue='Please select...'
                                        clientId='staples'
                                        isTransform={true}
                                    />
                                </FormGroup>
                            </Col>
                            <Col
                                md='3'
                                className='col-5ths'
                            >
                                <FormGroup>
                                    <Label
                                        className='form-label'
                                        aria-label='Program Category'
                                        for='programCategory'
                                    >
                                        Product Category
                                    </Label>
                                    <span
                                        aria-hidden='true'
                                        className='required-indicator'
                                    >
                                        *
                                    </span>
                                    <DropDownAutoComplete
                                        name='programCategory'
                                        id='programCategory'
                                        options={programCategories}
                                        onChangedValue={handleProgramCategoryChange}
                                        value={programCategory}
                                        defaultValue='Please select...'
                                        disabled={categoryDisabled}
                                        clientId='staples'
                                        isTransform={true}
                                    />
                                </FormGroup>
                            </Col>
                            {!attributeDisabled && (
                                <Col
                                    md='3'
                                    className={classNames('col-5ths', styles.programCategory)}
                                >
                                    <FormGroup>
                                        <Label
                                            className='form-label'
                                            aria-label='Program Category Attributes'
                                            for='programCategoryAttributes'
                                        >
                                            {programCategory}
                                        </Label>
                                        <span
                                            aria-hidden='true'
                                            className='required-indicator'
                                        >
                                            *
                                        </span>
                                        <DropDownAutoComplete
                                            name='programCategoryAttributes'
                                            id='programCategoryAttributes'
                                            options={attributes}
                                            onChangedValue={handleAttributeChange}
                                            value={tab?.priceToolRecords?.filterOptions?.attribute || attribute}
                                            defaultValue='Please select...'
                                            disabled={attributeDisabled}
                                            clientId='staples'
                                            isTransform={true}
                                        />
                                    </FormGroup>
                                </Col>
                            )}
                        </Row>
                        <Row className='multiColWrapper'>
                            <Col
                                md='3'
                                className={classNames('col-5ths', styles.multiSelect)}
                            >
                                <FormGroup>
                                    <Label
                                        className='form-label'
                                        aria-label='Retail Range'
                                        for='retailRange'
                                    >
                                        Retail Range
                                    </Label>
                                    <MultiDropDown
                                        options={retailPrices}
                                        onOptionClick={handleRetailPrice}
                                        selected={retailPrice}
                                        borderless={false}
                                        allSelectedOption='All Selected ...'
                                        hasSearch={true}
                                        disabled={listDisabled}
                                        clientId='staples'
                                    />
                                </FormGroup>
                            </Col>
                            <Col
                                md='3'
                                className='col-5ths'
                            >
                                <FormGroup>
                                    <Label
                                        className='form-label'
                                        aria-label='Term'
                                        for='term'
                                    >
                                        Term
                                    </Label>
                                    <MultiDropDown
                                        options={terms}
                                        onOptionClick={handleTerm}
                                        selected={term}
                                        borderless={false}
                                        allSelectedOption='All Selected ...'
                                        hasSearch={false}
                                        disabled={listDisabled}
                                        clientId='staples'
                                    />
                                </FormGroup>
                            </Col>
                            <Col
                                md='3'
                                className='col-5ths'
                            >
                                <FormGroup>
                                    <Label
                                        className='form-label'
                                        aria-label='Repair Type'
                                        for='repairType'
                                    >
                                        Repair Type
                                    </Label>
                                    <MultiDropDown
                                        options={repairTypes}
                                        onOptionClick={handleRepairType}
                                        selected={repairType}
                                        borderless={false}
                                        allSelectedOption='All Selected ...'
                                        hasSearch={false}
                                        disabled={listDisabled}
                                        clientId='staples'
                                    />
                                </FormGroup>
                            </Col>
                            <Col
                                md='3'
                                className='col-5ths'
                            >
                                <FormGroup>
                                    <Label
                                        className='form-label'
                                        aria-label='Coverage'
                                        for='coverage'
                                    >
                                        ADH Coverage
                                    </Label>
                                    <DropDownAutoComplete
                                        name='coverage'
                                        id='coverage'
                                        options={coverages}
                                        onChangedValue={handleCoverage}
                                        value={coverage}
                                        defaultValue='Please select...'
                                        disabled={listDisabled}
                                        isTransform={true}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row className='multiColWrapper'>
                            <Col className={classNames('d-flex justify-content-end', styles.btnCustomCss)}>
                                <Button
                                    className='btn-secondary mr-2'
                                    onClick={handleClear}
                                >
                                    Clear
                                </Button>
                                <SpinnerButton
                                    onClick={handleSearch}
                                    disabled={!validationCheck}
                                    spinning={showSpinner}
                                    styleType='primary'
                                    text='Search'
                                />
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                <Card className='mt-2'>
                    <ToolkitProvider
                        keyField={keyField}
                        columns={searchColumns || []}
                        bootstrap4
                        id='recTable'
                        defaultSorted={defaultSortedBy}
                        data={searchResults || []}
                        ref={(n) => (this.toolkit = n)}
                    >
                        {(props) => (
                            <PaginationProvider pagination={paginationFactory(options)}>
                                {({ paginationProps, paginationTableProps }) => (
                                    <CardBody>
                                        <Row className={classNames('pb-2', styles.rowDrdopdown)}>
                                            <Col sm='8'></Col>
                                            <Col
                                                sm='4'
                                                className={classNames(styles.priceTableTopContainer)}
                                            >
                                                <>
                                                    <div className={styles.dropdownLabel}>Rows per page: </div>
                                                    <div className={styles.customDropDown}>
                                                        <SizePerPageDropdownStandalone {...paginationProps} />
                                                    </div>
                                                    <div className={styles.export}>
                                                        <UncontrolledDropdown onToggle={() => {}}>
                                                            <DropdownToggle
                                                                tag='div'
                                                                className='d-flex justify-content-end'
                                                            >
                                                                <div className={styles.exportIcon}>
                                                                    <ExportIcon
                                                                        className={styles.toggleIcon}
                                                                        alt='toggle dropdown'
                                                                    />
                                                                </div>
                                                                <div className={styles.exportText}>Export</div>
                                                            </DropdownToggle>
                                                            <DropdownMenu right>
                                                                <DropdownItem onClick={() => handleExportAll()}>
                                                                    Export All
                                                                </DropdownItem>
                                                                <DropdownItem
                                                                    className={
                                                                        disableCsvExport ? styles.disableItem : ''
                                                                    }
                                                                    disabled={disableCsvExport}
                                                                    onClick={() => handleExport()}
                                                                >
                                                                    Export Selected
                                                                </DropdownItem>
                                                            </DropdownMenu>
                                                        </UncontrolledDropdown>
                                                    </div>
                                                </>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <>
                                                    <BootstrapTable
                                                        remote={false}
                                                        id='priceListTable'
                                                        wrapperClasses=''
                                                        classes={styles.pricingTable}
                                                        defaultSorted={defaultSortedBy}
                                                        keyField={keyField}
                                                        data={searchResults || []}
                                                        columns={searchColumns || []}
                                                        bordered={false}
                                                        selectRow={selectRow}
                                                        {...paginationTableProps}
                                                    />
                                                    <div>
                                                        <PaginationListStandalone {...paginationProps} />
                                                    </div>
                                                </>
                                            </Col>
                                        </Row>
                                        {searchResults.length === 0 && !noResultsFound && (
                                            <Row>
                                                <Col className={styles.searchText}>
                                                    <label>Select a category to view results.</label>
                                                </Col>
                                            </Row>
                                        )}
                                        {searchResults.length === 0 && noResultsFound && (
                                            <Row>
                                                <Col className={styles.searchText}>
                                                    <label>
                                                        No plans are available with the current search criteria.
                                                    </label>
                                                </Col>
                                            </Row>
                                        )}
                                    </CardBody>
                                )}
                            </PaginationProvider>
                        )}
                    </ToolkitProvider>
                </Card>
            </Container>
        </>
    );
};

export default PriceTool;
